import { createSelector } from "@reduxjs/toolkit";

/* =========================
   Helpers
   ========================= */
const pickCommentsState = (state) => {
  // ✅ compatible avec plusieurs montages dans configureStore
  return (
    state?.comments ||
    state?.comment ||
    state?.commentsSlice ||
    state?.commentsReducer ||
    {}
  );
};

const toKey = (v) => (v == null ? "" : String(v).trim());

/* ✅ normalize id (mongo uses _id) */
const getId = (c) => String(c?._id ?? c?.id ?? "");

/* =========================
   Base selector
   ========================= */
const selectCommentsState = (state) => pickCommentsState(state);

/* =========================
   Selectors
   ========================= */

// ✅ Sélectionner tous les commentaires d'un post
export const selectCommentsByPostId = createSelector(
  [selectCommentsState, (_, postId) => toKey(postId)],
  (state, postId) => {
    if (!postId) return [];

    const bucket = state?.byPostId?.[postId];

    // ✅ support: array direct OU {items:[...]} OU {comments:[...]}
    if (Array.isArray(bucket)) return bucket;
    if (Array.isArray(bucket?.items)) return bucket.items;
    if (Array.isArray(bucket?.comments)) return bucket.comments;

    // ✅ fallback simple (ancienne)
    if (state?.byPostId && Array.isArray(state.byPostId[postId])) {
      return state.byPostId[postId];
    }

    return [];
  }
);

// ✅ Sélectionner un commentaire spécifique
export const selectCommentById = createSelector(
  [
    selectCommentsState,
    (_, { postId, commentId }) => ({
      postId: toKey(postId),
      commentId: toKey(commentId),
    }),
  ],
  (state, { postId, commentId }) => {
    if (!postId || !commentId) return null;

    const bucket = state?.byPostId?.[postId];
    const arr = Array.isArray(bucket)
      ? bucket
      : Array.isArray(bucket?.items)
      ? bucket.items
      : Array.isArray(bucket?.comments)
      ? bucket.comments
      : [];

    // ✅ mongo: _id ; fallback: id
    return arr.find((c) => getId(c) === commentId) || null;
  }
);

// ✅ Sélectionner l'état de chargement
export const selectCommentsLoading = createSelector(
  [selectCommentsState],
  (state) => !!state?.loading
);

// ✅ Sélectionner l'erreur
export const selectCommentsError = createSelector(
  [selectCommentsState],
  (state) => state?.error || null
);

// ✅ Sélectionner le nombre de commentaires par post
export const selectCommentsCountByPostId = createSelector(
  [selectCommentsByPostId],
  (comments) => (Array.isArray(comments) ? comments.length : 0)
);
